<template>
  <Transition name="modal" :key="isOpen ? 'open' : 'closed'">
    <div
        v-if="isOpen"
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto"
        @click="closeModal"
    >
      <div
          class="bg-white rounded-lg shadow-xl w-full max-w-6xl mx-4 p-8 relative"
          @click.stop
      >
        <!-- Header -->
        <div class="flex justify-between items-center mb-8">
          <h2 class="w-full text-center text-xl font-semibold text-gray-800 tracking-wide">
            {{ props.beneficiary ? 'EDIT BENEFICIARY' : 'NEW BENEFICIARY' }}
          </h2>

          <button
              @click="closeModal"
              class="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <!-- Account Type Radio -->
        <div class="flex gap-6 mb-6">
          <label class="flex items-center gap-2">
            <input type="radio" value="business" v-model="formData.account_type" />
            Business
          </label>
          <label class="flex items-center gap-2">
            <input type="radio" value="personal" v-model="formData.account_type" />
            Personal
          </label>
        </div>

        <!-- Form -->
        <form @submit.prevent="handleSubmit" class="space-y-6">
          <!-- Title -->
          <FloatingLabelInput
              v-model="formData.name"
              input-id="name-field"
              label="Name *"
          />
          <p v-if="errors.name" class="text-red-500 text-sm">{{ errors.name }}</p>

          <!-- European -->
          <template v-if="formData.type === 'european'">
            <FloatingLabelInput
                v-model="formData.iban"
                input-id="iban-field"
                label="IBAN *"
            />
            <p v-if="errors.iban" class="text-red-500 text-sm">{{ errors.iban }}</p>

            <FloatingLabelInput
                v-model="formData.swift"
                input-id="swift-field"
                label="BIC / SWIFT Code *"
            />
            <p v-if="errors.swift" class="text-red-500 text-sm">{{ errors.swift }}</p>
          </template>

          <!-- International -->
          <template v-else-if="formData.type === 'international'">
            <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
              <!-- Account / IBAN -->
              <div>
                <FloatingLabelInput
                    v-model="formData.account_iban"
                    input-id="account-iban-field"
                    label="Account Number / IBAN *"
                />
                <p v-if="errors.account_iban" class="text-red-500 text-sm">{{ errors.account_iban }}</p>
              </div>

              <!-- Beneficiary Address -->
              <div>
                <FloatingLabelInput
                    v-model="formData.beneficiary_address"
                    input-id="beneficiary-address-field"
                    label="Beneficiary Address *"
                />
                <p v-if="errors.beneficiary_address" class="text-red-500 text-sm">{{ errors.beneficiary_address }}</p>
              </div>

              <!-- Beneficiary Bank Address -->
              <div>
                <FloatingLabelInput
                    v-model="formData.beneficiary_bank_address"
                    input-id="beneficiary-bank-address-field"
                    label="Beneficiary Bank Address *"
                />
                <p v-if="errors.beneficiary_bank_address" class="text-red-500 text-sm">{{ errors.beneficiary_bank_address }}</p>
              </div>

              <!-- SWIFT -->
              <div>
                <FloatingLabelInput
                    v-model="formData.swift"
                    input-id="swift-field"
                    label="BIC / SWIFT Code *"
                />
                <p v-if="errors.swift" class="text-red-500 text-sm">{{ errors.swift }}</p>
              </div>

              <!-- Currency Dropdown -->
              <div>
                <select
                    id="currency"
                    v-model="formData.currency"
                    class="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="">Select currency</option>
                  <option value="USD">USD - Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - Pound</option>
                </select>
                <p v-if="errors.currency" class="text-red-500 text-sm">{{ errors.currency }}</p>
              </div>

              <!-- City -->
              <div>
                <FloatingLabelInput
                    v-model="formData.city"
                    input-id="city-field"
                    label="City *"
                />
                <p v-if="errors.city" class="text-red-500 text-sm">{{ errors.city }}</p>
              </div>

              <!-- Country -->
<!--              <div>-->
<!--                <FloatingLabelInput-->
<!--                    v-model="formData.country"-->
<!--                    input-id="country-field"-->
<!--                    label="Country *"-->
<!--                />-->
<!--                <p v-if="errors.country" class="text-red-500 text-sm">{{ errors.country }}</p>-->
<!--              </div>-->

              <div class="relative w-full">
                <input
                    id="country-search"
                    v-model="searchQuery"
                    type="text"
                    placeholder="Select Country *"
                    class="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent peer"
                    @focus="dropdownOpen = true"
                    @blur="closeDropdown"
                />

                <ul
                    v-if="dropdownOpen && filteredCountries.length"
                    class="absolute z-10 w-full bg-white border border-gray-300 rounded-md max-h-48 overflow-y-auto mt-1 shadow-lg"
                >
                  <li
                      v-for="country in filteredCountries"
                      :key="country"
                      @mousedown.prevent="selectCountry(country)"
                      class="px-4 py-3 hover:bg-gray-100 cursor-pointer text-sm"
                  >
                    {{ country }}
                  </li>
                </ul>
              </div>

              <!-- Post Code -->
              <div>
                <FloatingLabelInput
                    v-model="formData.post_code"
                    input-id="post-code-field"
                    label="Post Code *"
                />
                <p v-if="errors.post_code" class="text-red-500 text-sm">{{ errors.post_code }}</p>
              </div>

              <!-- Intermediary Bank Name (Optional) -->
              <div>
                <FloatingLabelInput
                    v-model="formData.intermediary_bank_name"
                    input-id="intermediary-bank-name-field"
                    label="Intermediary Bank Name"
                />
              </div>

              <!-- Intermediary Bank Code / BIC (Optional) -->
              <div>
                <FloatingLabelInput
                    v-model="formData.intermediary_bank_code"
                    input-id="intermediary-bank-code-field"
                    label="Intermediary Bank Code / BIC"
                />
              </div>
            </div>
          </template>

          <!-- Internal -->
          <template v-else-if="formData.type === 'internal'">
            <FloatingLabelInput
                v-model="formData.account_number"
                input-id="account-field"
                label="Account Number *"
            />
            <p v-if="errors.account_number" class="text-red-500 text-sm">{{ errors.account_number }}</p>
          </template>

          <!-- Additional Info (Optional in all types) -->
          <div class="mt-4">
            <textarea
                v-model="formData.additional_info"
                class="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                placeholder="Additional Information"
                rows="3"></textarea>
          </div>

          <p v-if="errors.general" class="text-red-500 text-sm">{{ errors.general }}</p>

          <!-- Confirm Button -->
          <div class="flex justify-end pt-4">
            <button type="submit" class="bg-gray-800 hover:bg-gray-600 text-white font-semibold px-8 py-3 rounded-md">
              {{ props.beneficiary ? 'UPDATE' : 'CONFIRM' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import FloatingLabelInput from '@/components/ui/FloatingLabelInput.vue'
import {defineProps, ref, defineEmits, watch, computed} from 'vue'
import api from '@/services/api'

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  type: { type: String, default: 'european' },
  beneficiary: { type: Object, default: null },
})

const emit = defineEmits(['close', 'submitted'])

const formData = ref({
  name: '',
  account_type: 'business',
  account_number: '',
  iban: '',
  account_iban: '',
  beneficiary_address: '',
  beneficiary_bank_address: '',
  swift: '',
  currency: '',
  city: '',
  country: '',
  post_code: '',
  intermediary_bank_name: '',
  intermediary_bank_code: '',
  additional_info: '',
  type: props.type,
})


const nonEuCountries = [
  "Afghanistan",
  "Albania",
  "Algeria",
  "Andorra",
  "Angola",
  "Antigua and Barbuda",
  "Argentina",
  "Armenia",
  "Australia",
  "Azerbaijan",
  "Bahamas",
  "Bahrain",
  "Bangladesh",
  "Barbados",
  "Belarus",
  "Belize",
  "Benin",
  "Bhutan",
  "Bolivia",
  "Bosnia and Herzegovina",
  "Botswana",
  "Brazil",
  "Brunei",
  "Burkina Faso",
  "Burundi",
  "Cabo Verde",
  "Cambodia",
  "Cameroon",
  "Canada",
  "Central African Republic",
  "Chad",
  "Chile",
  "China",
  "Colombia",
  "Comoros",
  "Congo",
  "Costa Rica",
  "Cuba",
  "Djibouti",
  "Dominica",
  "Dominican Republic",
  "Ecuador",
  "Egypt",
  "El Salvador",
  "Equatorial Guinea",
  "Eritrea",
  "Eswatini",
  "Ethiopia",
  "Fiji",
  "Gabon",
  "Gambia",
  "Georgia",
  "Ghana",
  "Grenada",
  "Guatemala",
  "Guinea",
  "Guinea-Bissau",
  "Guyana",
  "Haiti",
  "Honduras",
  "Iceland",
  "India",
  "Indonesia",
  "Iran",
  "Iraq",
  "Israel",
  "Jamaica",
  "Japan",
  "Jordan",
  "Kazakhstan",
  "Kenya",
  "Kiribati",
  "Kuwait",
  "Kyrgyzstan",
  "Laos",
  "Lebanon",
  "Lesotho",
  "Liberia",
  "Libya",
  "Liechtenstein",
  "Madagascar",
  "Malawi",
  "Malaysia",
  "Maldives",
  "Mali",
  "Marshall Islands",
  "Mauritania",
  "Mauritius",
  "Micronesia",
  "Moldova",
  "Monaco",
  "Mongolia",
  "Montenegro",
  "Morocco",
  "Mozambique",
  "Myanmar",
  "Namibia",
  "Nauru",
  "Nepal",
  "New Zealand",
  "Nicaragua",
  "Niger",
  "Nigeria",
  "North Korea",
  "North Macedonia",
  "Norway",
  "Oman",
  "Pakistan",
  "Palau",
  "Palestine",
  "Panama",
  "Papua New Guinea",
  "Paraguay",
  "Peru",
  "Philippines",
  "Qatar",
  "Russia",
  "Rwanda",
  "Saint Kitts and Nevis",
  "Saint Lucia",
  "Saint Vincent and the Grenadines",
  "Samoa",
  "San Marino",
  "Sao Tome and Principe",
  "Saudi Arabia",
  "Senegal",
  "Serbia",
  "Seychelles",
  "Sierra Leone",
  "Singapore",
  "Solomon Islands",
  "Somalia",
  "South Africa",
  "South Korea",
  "South Sudan",
  "Sri Lanka",
  "Sudan",
  "Suriname",
  "Switzerland",
  "Syria",
  "Taiwan",
  "Tajikistan",
  "Tanzania",
  "Thailand",
  "Timor-Leste",
  "Togo",
  "Tonga",
  "Trinidad and Tobago",
  "Tunisia",
  "Turkey",
  "Turkmenistan",
  "Tuvalu",
  "Uganda",
  "Ukraine",
  "United Arab Emirates",
  "United Kingdom",
  "United States",
  "Uruguay",
  "Uzbekistan",
  "Vanuatu",
  "Vatican City",
  "Venezuela",
  "Vietnam",
  "Yemen",
  "Zambia",
  "Zimbabwe"
];


const searchQuery = ref('')
const selectedCountry = ref('')
const dropdownOpen = ref(false)

const filteredCountries = computed(() => {
  if (!searchQuery.value) return nonEuCountries
  return nonEuCountries.filter(c =>
      c.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})

const selectCountry = (country) => {
  searchQuery.value = country
  selectedCountry.value = country
  formData.value.country = country   // ← اضافه کردن این خط
  dropdownOpen.value = false
}


const closeDropdown = () => {
  setTimeout(() => {
    dropdownOpen.value = false
  }, 150) // تا کلیک روی گزینه ثبت شود
}



const errors = ref({})

watch(
    () => props.type,
    (newType) => {
      formData.value.type = newType
    },
    { immediate: true }
)

const closeModal = () => emit('close')

const resetForm = () => {
  formData.value = {
    name: '',
    account_type: 'business',
    account_number: '',
    iban: '',
    account_iban: '',
    beneficiary_address: '',
    beneficiary_bank_address: '',
    swift: '',
    currency: '',
    city: '',
    country: '',
    post_code: '',
    intermediary_bank_name: '',
    intermediary_bank_code: '',
    additional_info: '',
    type: props.type,
  }
  errors.value = {}
}

watch(
    () => props.beneficiary,
    (bene) => {
      if (bene) {
        resetForm()
        formData.value = {
          ...formData.value,
          ...bene,
          type: bene.type || props.type,
        }
      } else {
        resetForm()
      }
    },
    { immediate: true }
)

const validateForm = () => {
  errors.value = {}

  if (!formData.value.name) errors.value.name = 'Title is required'

  switch (formData.value.type) {
    case 'european':
      if (!formData.value.iban) errors.value.iban = 'IBAN is required'
      break

    case 'international':
      if (!formData.value.account_iban) errors.value.account_iban = 'Account Number / IBAN is required'
      if (!formData.value.beneficiary_address) errors.value.beneficiary_address = 'Beneficiary Address is required'
      if (!formData.value.beneficiary_bank_address) errors.value.beneficiary_bank_address = 'Beneficiary Bank Address is required'
      if (!formData.value.swift) errors.value.swift = 'BIC / SWIFT Code is required'
      if (!formData.value.currency) errors.value.currency = 'Currency is required'
      if (!formData.value.city) errors.value.city = 'City is required'
      if (!formData.value.country) errors.value.country = 'Country is required'
      if (!formData.value.post_code) errors.value.post_code = 'Post Code is required'


        console.log("ascadcd",formData);
      break

    case 'internal':
      if (!formData.value.account_number) errors.value.account_number = 'Account Number is required'
      break
  }

  return Object.keys(errors.value).length === 0
}

const handleSubmit = async () => {
  console.log("sassssss_aaa","aaa");
  if (!validateForm()) return
  console.log("sassssss_aaa","bbb");
  errors.value = {}
  try {
    const token = localStorage.getItem('token')
    let res
    if (props.beneficiary && props.beneficiary.id) {
      res = await api.put(`/beneficiaries/${props.beneficiary.id}`, formData.value, {
        headers: { Authorization: `Bearer ${token}` },
      })
    } else {
      res = await api.post('/beneficiaries', formData.value, {
        headers: { Authorization: `Bearer ${token}` },
      })
    }

    emit('submitted', res.data)

    console.log("sassssss",res);
    resetForm()
    closeModal()
  } catch (err) {
    const data = err.response?.data
    if (data?.errors) {
      errors.value = { ...data.errors }
    } else if (data?.message) {
      errors.value.general = data.message
    } else {
      errors.value.general = props.beneficiary
          ? 'Failed to update beneficiary.'
          : 'Failed to create beneficiary.'
    }
  }
}
</script>
